import { DecisionProgress, DecisionState, DocumentationDecision, DocumentationThread } from '../interfaces/documentation.interface'

/**
 * Saca del manual las decisiones que Contabilidad tiene que contestar.
 *
 * Cada tarjeta se marca en el HTML así:
 *
 *     <div class="dec" data-block="decisiones-b07" id="dec-p1">
 *       <div class="top"><span class="id">P1</span><span class="ti">Depósitos duplicados</span>…
 *
 * El `data-block` es lo que ancla los comentarios, así que **la tarjeta es a la vez la decisión
 * y la unidad comentable**: no hace falta marcar nada aparte para saber si alguien la contestó.
 *
 * Se deriva del asset y no se declara en el catálogo a propósito: un listado paralelo se
 * desfasaría en silencio en cuanto alguien agregue o quite una decisión de un manual.
 */
export const extractDecisions = (html: string): DocumentationDecision[] => {
  const parsed = new DOMParser().parseFromString(html, 'text/html')

  return Array.from(parsed.querySelectorAll<HTMLElement>('[id^="dec-"]')).reduce<DocumentationDecision[]>((acc, card) => {
    const blockId = card.dataset['block']
    //* Sin `data-block` la tarjeta no es comentable, así que no se puede medir: se omite en
    //* vez de contarla como pendiente para siempre.
    if (!blockId) return acc

    acc.push({
      code: (card.querySelector('.id')?.textContent ?? '').trim() || card.id.replace(/^dec-/, '').toUpperCase(),
      anchor: card.id,
      blockId,
      title: (card.querySelector('.ti')?.textContent ?? '').replace(/\s+/g, ' ').trim(),
    })
    return acc
  }, [])
}

/**
 * En qué punto está una decisión. Los dos umbrales son distintos a propósito:
 *
 * - `answered` — Contabilidad dejó su respuesta. Es SU meta.
 * - `closed`   — nosotros la leímos y la resolvimos. Es LA NUESTRA.
 *
 * Un hilo resuelto sin comentarios no debería existir (crear un hilo exige contenido), pero si
 * apareciera cuenta como cerrado: alguien decidió que ahí ya no hay nada que discutir.
 */
export const decisionState = (threads: DocumentationThread[], blockId: string): DecisionState => {
  const propios = threads.filter((thread) => thread.blockId === blockId)
  if (!propios.length) return 'pending'
  //* Basta uno abierto para que siga habiendo conversación viva.
  return propios.every((thread) => thread.status === 'RESOLVED') ? 'closed' : 'answered'
}

/** `closed` cuenta también como contestada: no se puede cerrar lo que nadie contestó. */
export const decisionProgress = (decisions: DocumentationDecision[], threads: DocumentationThread[]): DecisionProgress =>
  decisions.reduce<DecisionProgress>(
    (acc, decision) => {
      const state = decisionState(threads, decision.blockId)
      if (state !== 'pending') acc.answered++
      if (state === 'closed') acc.closed++
      return acc
    },
    { total: decisions.length, answered: 0, closed: 0 }
  )

export const DECISION_STATE_LABEL: Record<DecisionState, string> = {
  pending: 'Sin contestar',
  answered: 'Contestada',
  closed: 'Cerrada',
}
